import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import IntegrationConfigWrapper from '@/components/organisms/Integrations/Components/Integration';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { IntegrationStatus, SlackIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { InstallationSection } from './components/InstallationSection';
import { SlackConfiguration } from './components/SlackConfiguration';

const IntegrationSlack = () => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const deskId = useDeskId();
  const { integrationId } = useParams();
  const { integration } = useIntegrations<SlackIntegration>(deskId, integrationId);

  const prevStatusRef = useRef(integration?.status);

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    const currentStatus = integration?.status;

    if (
      prevStatus === IntegrationStatus.VERIFIED &&
      currentStatus &&
      currentStatus !== IntegrationStatus.VERIFIED
    ) {
      toast.warning(t('integrations.slack.connection_lost'));
    }

    prevStatusRef.current = currentStatus;
  }, [integration?.status, t]);

  if (!integration) {
    return null;
  }

  return (
    <IntegrationConfigWrapper integration={integration}>
      <InstallationSection integration={integration} />
      <SlackConfiguration
        key={`${integration?.config?.signing_secret}-${integration?.config?.access_token}`}
        integration={integration}
        canWrite={canWrite}
      />
    </IntegrationConfigWrapper>
  );
};

export default IntegrationSlack;
